import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Headphones, Mic, MicOff, X, Loader2, Megaphone, MessageSquare } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { usePlivo } from "@/lib/plivo-context";
import { useToast } from "@/hooks/use-toast";

type Mode = "listen" | "barge" | "whisper";

export function MonitorCallPanel() {
  const { monitor, hangupMonitor } = usePlivo();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [monitorElapsed, setMonitorElapsed] = useState(0);
  const [callElapsed, setCallElapsed] = useState(0);

  useEffect(() => {
    if (!monitor.active || !monitor.connectedAt) {
      setMonitorElapsed(0);
      return;
    }
    const start = monitor.connectedAt;
    const t = setInterval(() => setMonitorElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(t);
  }, [monitor.active, monitor.connectedAt]);

  useEffect(() => {
    if (!monitor.active || !monitor.callStartedAt) {
      setCallElapsed(0);
      return;
    }
    const start = monitor.callStartedAt;
    const tick = () => setCallElapsed(Math.floor((Date.now() - start) / 1000));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [monitor.active, monitor.callStartedAt]);

  if (!monitor.active) return null;

  const fmtSec = (s: number) => {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${r.toString().padStart(2, "0")}`;
  };

  const changeMode = async (mode: Mode) => {
    if (!monitor.sessionId || mode === monitor.mode) return;
    setBusy(true);
    try {
      await apiRequest("POST", `/api/calls/monitor/${monitor.sessionId}/mode`, { mode });
      monitor.setMode(mode);
      toast({ title: `Mode: ${mode}` });
    } catch (e: any) {
      // A failed whisper entry may have safely fallen back to listen, or may
      // retain recovery intent. Show the server state instead of the old badge.
      try {
        const response = await apiRequest("GET", `/api/calls/${monitor.callLogId}/monitor/active`);
        const current = await response.json();
        if (current.active && current.sessionId === monitor.sessionId) monitor.setMode(current.mode);
      } catch {
        // Keep the original error visible if reconciliation is unavailable.
      }
      toast({ title: "Failed to change mode", description: e.message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const end = async () => {
    setBusy(true);
    try {
      if (monitor.sessionId) {
        await apiRequest("POST", `/api/calls/monitor/${monitor.sessionId}/end`);
      }
      hangupMonitor();
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard-stats"] });
    } catch (e: any) {
      toast({ title: "Failed to stop monitoring", description: e.message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const modeBadge: Record<Mode, { label: string; className: string }> = {
    listen: { label: "Listening", className: "bg-blue-500/10 text-blue-600 border-blue-200 dark:border-blue-900" },
    barge: { label: "Barging", className: "bg-red-500/10 text-red-600 border-red-200 dark:border-red-900" },
    whisper: { label: "Whispering", className: "bg-amber-500/10 text-amber-600 border-amber-200 dark:border-amber-900" },
  };
  const badge = modeBadge[monitor.mode];

  return (
    <Card
      className="fixed bottom-6 right-6 z-[70] w-[320px] shadow-2xl border-2 border-indigo-500/40"
      data-testid="panel-monitor-call"
    >
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="p-2 rounded-md bg-indigo-500/10 shrink-0">
              <Headphones className="w-4 h-4 text-indigo-600" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">Monitoring call</p>
              <p className="text-xs text-muted-foreground truncate" data-testid="text-monitor-target">
                {monitor.targetName || monitor.contactNumber || "Live call"}
              </p>
            </div>
          </div>
          <Badge variant="outline" className={`text-[10px] ${badge.className}`} data-testid="badge-monitor-mode">
            {badge.label}
          </Badge>
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="rounded border p-2">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Agent</p>
            <p className="truncate font-medium" data-testid="text-monitor-agent">{monitor.agentName || "—"}</p>
          </div>
          <div className="rounded border p-2">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Contact</p>
            <p className="truncate font-medium" data-testid="text-monitor-contact">{monitor.contactNumber || monitor.targetName || "—"}</p>
          </div>
          <div className="rounded border p-2">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Call duration</p>
            <p className="font-mono font-medium" data-testid="text-monitor-call-duration">{monitor.callStartedAt ? fmtSec(callElapsed) : "—"}</p>
          </div>
          <div className="rounded border p-2">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Monitor time</p>
            <p className="font-mono font-medium" data-testid="text-monitor-elapsed">{monitor.connectedAt ? fmtSec(monitorElapsed) : "Connecting…"}</p>
          </div>
        </div>

        {monitor.mode === "whisper" && (
          <div
            className="rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 px-2 py-1.5 text-[11px] text-amber-700 dark:text-amber-300"
            data-testid="badge-customer-on-hold"
          >
            Customer is on hold — only the agent can hear you.
          </div>
        )}

        <div className="grid grid-cols-3 gap-2">
          <Button
            size="sm"
            variant={monitor.mode === "listen" ? "default" : "outline"}
            onClick={() => changeMode("listen")}
            disabled={busy}
            data-testid="button-monitor-listen"
            className="text-xs"
          >
            <MicOff className="w-3.5 h-3.5 mr-1" />
            Listen
          </Button>
          <Button
            size="sm"
            variant={monitor.mode === "whisper" ? "default" : "outline"}
            onClick={() => changeMode("whisper")}
            disabled={busy}
            data-testid="button-monitor-whisper"
            className="text-xs"
          >
            <MessageSquare className="w-3.5 h-3.5 mr-1" />
            Whisper
          </Button>
          <Button
            size="sm"
            variant={monitor.mode === "barge" ? "default" : "outline"}
            onClick={() => changeMode("barge")}
            disabled={busy}
            data-testid="button-monitor-barge"
            className="text-xs"
          >
            <Megaphone className="w-3.5 h-3.5 mr-1" />
            Barge
          </Button>
        </div>

        <Button
          size="sm"
          variant="destructive"
          onClick={end}
          disabled={busy}
          className="w-full"
          data-testid="button-monitor-end"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <X className="w-3.5 h-3.5 mr-1" />}
          Stop monitoring
        </Button>
      </div>
    </Card>
  );
}

export function MonitorStartButton({ callLogId, contactLabel }: { callLogId: string; contactLabel?: string | null }) {
  const { monitor, startMonitor, expectMonitorCall, cancelMonitorExpect } = usePlivo();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const isThisActive = monitor.active && monitor.callLogId === callLogId;

  const onClick = async () => {
    if (isThisActive || monitor.active) return;
    setBusy(true);
    // Signal BEFORE the HTTP request — Plivo sends the SIP INVITE to the browser
    // concurrently with the API response, so we must be ready to recognise and
    // auto-answer the incoming call even while the round-trip is in flight.
    expectMonitorCall();
    try {
      const res = await apiRequest("POST", `/api/calls/${callLogId}/monitor/start`, { mode: "listen" });
      const data = await res.json();
      startMonitor({
        sessionId: data.sessionId,
        callLogId,
        mode: data.mode || "listen",
        targetName: data.contactName || contactLabel || null,
        agentName: data.agentName || null,
        contactNumber: data.contactNumber || null,
        callStartedAt: data.callStartedAt || null,
      });
    } catch (e: any) {
      cancelMonitorExpect();
      toast({ title: "Failed to start monitoring", description: e.message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      size="icon"
      variant="ghost"
      className="h-7 w-7"
      onClick={onClick}
      disabled={busy || monitor.active}
      data-testid={`button-monitor-${callLogId}`}
      title={monitor.active ? "Already monitoring a call" : "Listen / Barge / Whisper"}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Headphones className="w-3.5 h-3.5" />}
    </Button>
  );
}
